import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ShieldCheck, LogIn, ArrowLeft } from "lucide-react";
import { formatApiError } from "@/lib/api";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import PublicFooter from "@/components/PublicFooter";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const u = await login(username.trim(), password);
      if (u.must_change_password) {
        nav("/cambio-password", { replace: true });
      } else {
        nav(u.role === "admin" ? "/admin" : "/", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    <div className="min-h-screen flex items-stretch">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between auth-bg w-1/2 p-12 text-white relative overflow-hidden">
        <Link to="/" data-testid="login-brand-home" className="flex items-center gap-3 relative z-10 group">
          <div className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center border border-white/15 group-hover:bg-white/20 transition-colors">
            <ShieldCheck className="w-5 h-5 text-blue-300" />
          </div>
          <div className="font-heading font-bold text-xl tracking-tight group-hover:text-blue-200 transition-colors">incassi.cloud</div>
        </Link>
        <div className="relative z-10 max-w-md">
          <h1 className="font-heading text-4xl xl:text-5xl font-bold leading-tight">
            Comunicazione incassi <span className="text-blue-300">precisa</span> e ordinata, ogni giorno.
          </h1>
          <p className="text-slate-300 mt-6 text-base leading-relaxed">
            Foglio cassa giornaliero per i collaboratori, chiusura automatica, notifiche urgenti e riepiloghi in PDF/Excel — tutto nel rispetto della tua agenzia.
          </p>
          <div className="mt-10 grid grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-blue-300 font-heading font-semibold">Sicuro</div>
              <div className="text-slate-400">Autenticazione con ruoli</div>
            </div>
            <div>
              <div className="text-blue-300 font-heading font-semibold">Automatico</div>
              <div className="text-slate-400">Chiusura programmata</div>
            </div>
            <div>
              <div className="text-blue-300 font-heading font-semibold">Tracciato</div>
              <div className="text-slate-400">Storico completo</div>
            </div>
            <div>
              <div className="text-blue-300 font-heading font-semibold">Conforme</div>
              <div className="text-slate-400">Europe/Rome</div>
            </div>
          </div>
        </div>
        <div className="text-xs text-slate-400 relative z-10">© Incassi Giornalieri — Agenzia Assicurativa</div>
      </div>

      {/* Login form */}
      <div className="flex-1 flex items-center justify-center bg-slate-50 px-6 py-12">
        <Card className="w-full max-w-md p-8 card-elevated border-slate-200">
          <Link to="/" data-testid="login-brand-home-mobile" className="lg:hidden flex items-center gap-2 mb-6 hover:opacity-80 transition-opacity">
            <ShieldCheck className="w-5 h-5 text-slate-900" />
            <span className="font-heading font-bold">incassi.cloud</span>
          </Link>
          <Link
            to="/"
            data-testid="login-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 mb-4 group"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" aria-hidden="true" /> Torna alla landing
          </Link>
          <h2 className="font-heading text-2xl font-bold text-slate-900">Accedi</h2>
          <p className="text-sm text-slate-500 mt-1 mb-6">Inserisci le tue credenziali per continuare.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-slate-700">Username</Label>
              <Input
                id="username"
                data-testid="login-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="es. m.bianchi"
                required
                autoFocus
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
              />
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </div>
            )}

            <Button
              type="submit"
              data-testid="login-submit"
              disabled={submitting}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white"
            >
              <LogIn className="w-4 h-4 mr-2" />
              {submitting ? "Accesso in corso…" : "Accedi"}
            </Button>

            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => setForgotOpen(true)}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                data-testid="forgot-password-link"
              >
                Hai dimenticato la password?
              </button>
            </div>
            <div className="text-center pt-2 border-t border-slate-100">
              <p className="text-sm text-slate-600 mt-3">
                Non hai ancora un account?{" "}
                <Link
                  to="/registrazione"
                  data-testid="login-signup-link"
                  className="text-blue-600 hover:text-blue-800 hover:underline font-semibold"
                >
                  Registrati
                </Link>
              </p>
            </div>
          </form>
        </Card>
      </div>

      <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen} />
    </div>
    <PublicFooter />
    </>
  );
}
